/**
 * 全局错误处理工具类
 * 用于统一处理各种错误情况
 */
class ErrorHandler {
  constructor() {
    this.errorCount = 0;
    this.maxErrorCount = 10; // 最大错误次数
    this.errorResetTime = 5 * 60 * 1000; // 5分钟后重置错误计数
  }

  /**
   * 处理网络错误
   * @param {Error} error 错误对象
   * @param {string} context 错误上下文
   */
  handleNetworkError(error, context = '') {
    this.errorCount++;
    
    console.error(`网络错误 [${context}]:`, error);
    
    // 检查是否超过最大错误次数
    if (this.errorCount >= this.maxErrorCount) {
      console.error('错误次数过多，暂停网络请求');
      this.showErrorMessage('网络连接不稳定，请稍后重试');
      return false;
    }
    
    // 根据错误类型提供不同的处理建议
    if (error.message.includes('ERR_CONNECTION_CLOSED')) {
      this.showErrorMessage('网络连接被关闭，请检查网络设置');
    } else if (error.message.includes('Failed to fetch')) {
      this.showErrorMessage('网络请求失败，请检查网络连接');
    } else if (error.message.includes('timeout')) {
      this.showErrorMessage('请求超时，请稍后重试');
    } else {
      this.showErrorMessage('网络错误，请稍后重试');
    }
    
    return true;
  }

  /**
   * 处理图片加载错误
   * @param {Error} error 错误对象
   * @param {string} imageUrl 图片URL
   */
  handleImageError(error, imageUrl) {
    console.warn(`图片加载失败: ${imageUrl}`, error);
    
    // 记录失败的图片URL
    this.recordFailedImage(imageUrl);
    
    return true;
  }

  /**
   * 记录失败的图片URL
   * @param {string} imageUrl 图片URL
   */
  recordFailedImage(imageUrl) {
    const failedImages = JSON.parse(localStorage.getItem('failed_images') || '[]');
    
    if (!failedImages.includes(imageUrl)) {
      failedImages.push(imageUrl);
      // 只保留最近50个失败的图片URL
      if (failedImages.length > 50) {
        failedImages.splice(0, failedImages.length - 50);
      }
      localStorage.setItem('failed_images', JSON.stringify(failedImages));
    }
  }

  /**
   * 检查图片是否之前加载失败过
   * @param {string} imageUrl 图片URL
   * @returns {boolean} 是否之前失败过
   */
  isImagePreviouslyFailed(imageUrl) {
    const failedImages = JSON.parse(localStorage.getItem('failed_images') || '[]');
    return failedImages.includes(imageUrl);
  }

  /**
   * 显示错误消息
   * @param {string} message 错误消息
   */
  showErrorMessage(message) {
    // 使用Vant的Toast组件显示错误消息
    if (window.Vue && window.Vue.prototype.$toast) {
      window.Vue.prototype.$toast.fail(message);
    } else {
      console.error(message);
    }
  }

  /**
   * 重置错误计数
   */
  resetErrorCount() {
    this.errorCount = 0;
    console.log('错误计数已重置');
  }

  /**
   * 获取错误统计
   * @returns {Object} 错误统计信息
   */
  getErrorStats() {
    const failedImages = JSON.parse(localStorage.getItem('failed_images') || '[]');
    
    return {
      errorCount: this.errorCount,
      maxErrorCount: this.maxErrorCount,
      failedImagesCount: failedImages.length,
      failedImages: failedImages.slice(-10) // 最近10个失败的图片
    };
  }

  /**
   * 清理失败的图片记录
   */
  clearFailedImages() {
    localStorage.removeItem('failed_images');
    console.log('已清理失败的图片记录');
  }

  /**
   * 检查网络状态
   * @returns {Promise<boolean>} 网络是否可用
   */
  async checkNetworkStatus() {
    try {
      await fetch('/api/system/ping', {
        method: 'HEAD',
        mode: 'no-cors',
        cache: 'no-cache'
      });
      return true;
    } catch (error) {
      return false;
    }
  }

  /**
   * 处理API错误
   * @param {Object} error 错误对象
   * @param {string} apiName API名称
   */
  handleApiError(error, apiName = '') {
    console.error(`API错误 [${apiName}]:`, error);
    
    if (error.response) {
      // 服务器响应了错误状态码
      const status = error.response.status;
      const message = error.response.data?.msg || error.message;
      
      switch (status) {
        case 401:
          this.showErrorMessage('登录已过期，请重新登录');
          // 清除本地存储并跳转到登录页
          localStorage.clear();
          if (window.location.pathname !== '/Login') {
            window.location.href = '/Login';
          }
          break;
        case 403:
          this.showErrorMessage('没有权限访问此资源');
          break;
        case 404:
          this.showErrorMessage('请求的资源不存在');
          break;
        case 500:
          this.showErrorMessage('服务器内部错误，请稍后重试');
          break;
        default:
          this.showErrorMessage(message || '请求失败，请稍后重试');
      }
    } else if (error.request) {
      // 请求已发出但没有收到响应
      this.handleNetworkError(error, apiName);
    } else {
      // 其他错误
      this.showErrorMessage('请求配置错误');
    }
  }
}

// 创建全局错误处理实例
const errorHandler = new ErrorHandler();

// 定期重置错误计数
setInterval(() => {
  errorHandler.resetErrorCount();
}, errorHandler.errorResetTime);

export default errorHandler;
